// src/hooks/useAccessLevel.js
//
// The one place the app asks "what is this person allowed to do?"
//
// Everything about the free tier is decided in the CAPABILITIES table below.
// Nothing else in the app should compare a level directly — call the helpers,
// and the policy stays changeable in one edit.
//
// ===========================================================================
// WHAT CHANGED, AND WHY A PAID ACCOUNT STILL READ AS UNPAID
// ===========================================================================
// This hook used to decide "have they paid?" with exactly one expression:
//
//     subscription?.active
//
// where `subscription` came only from `subscriptions/{uid}`. That single
// field has three separate ways of being false for someone who has genuinely
// paid, and a checkout that starts with a free trial trips at least one of
// them every time:
//
//   1. A subscription that starts with a trial has Stripe status "trialing",
//      not "active". A webhook that writes `active: status === "active"`
//      therefore writes `active: false` for all 30 days of the trial — the
//      customer has entered a card and committed, and the app calls them
//      free.
//
//   2. The webhook also writes the legacy `users/{uid}.subscription` block
//      that the older dashboards read. If the canonical write fails, or the
//      Firestore rules don't grant the client read access to
//      `subscriptions/{uid}`, the legacy copy is sitting right there in a
//      document we already have open — and nothing looked at it. Note the
//      onSnapshot error handler below: a rules rejection sets the
//      subscription to null *silently*, which is indistinguishable from
//      never having paid.
//
//   3. `users/{uid}.role` being a granted string like "va-3rd-educator" is
//      itself proof of payment — the client cannot write that field, only
//      the webhook and claimSeat can. It was being ignored as evidence.
//
// So paid status is now the OR of all three signals. Any one of them is
// enough, which means a single broken write no longer locks a paying customer
// out of what they bought.

import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase/firebaseConfig";
import { isGrantedRole, effectiveRoleKind, portalViewFor, ROLE_KIND } from "../utils/accountRole";
import { subscriptionCoversCourse, purchasedCourses } from "../config/plans";
import { isEmailConfirmed } from "../utils/studentLogin";

export const ACCESS = {
  ANONYMOUS: "anonymous",   // not signed in
  UNVERIFIED: "unverified", // signed in, email not confirmed
  FREE: "free",             // verified, no active subscription — limited access
  PAID: "paid",             // active subscription, or holds an assigned seat
  ADMIN: "admin",
};

// Stripe subscription statuses that mean "this person is a customer".
//
// "trialing" is in the list deliberately: a card is on file, the subscription
// exists, and Stripe will bill it. Excluding it was the single biggest cause
// of paid accounts showing as unpaid.
//
// "past_due" is NOT here. Access continues until Stripe gives up and moves
// the subscription to "canceled" or "unpaid", but treating past_due as fully
// paid would hide the Subscribe prompt from someone whose card just failed —
// exactly the person who needs to see it.
export const LIVE_SUBSCRIPTION_STATUSES = ["active", "trialing"];

// ===========================================================================
// FREE TIER POLICY
// ===========================================================================
// A free account gets a real taste of the product — a few tools per subject
// in their own state and grade, with the games and quizzes attached to them —
// but nothing involving other people's data or their own uploads.
export const FREE_TOPIC_LIMIT = 6;

// Old name kept as an alias so nothing that imported it breaks.
export const FREE_TOOL_LIMIT = FREE_TOPIC_LIMIT;

const CAPABILITIES = {
  [ACCESS.ANONYMOUS]: {
    viewContent: false,
    contentLimit: 0,
    classroomTools: false,   // create classrooms, assign work
    studentTracking: false,  // see other people's progress
    authoring: false,        // slideshow upload, sync editor, layout builder
    customContent: false,    // paid custom material requests
    manageSeats: false,
  },
  [ACCESS.UNVERIFIED]: {
    viewContent: false,
    contentLimit: 0,
    classroomTools: false,
    studentTracking: false,
    authoring: false,
    customContent: false,
    manageSeats: false,
  },
  [ACCESS.FREE]: {
    viewContent: true,
    contentLimit: FREE_TOPIC_LIMIT,
    classroomTools: false,
    studentTracking: false,
    authoring: false,
    customContent: false,
    manageSeats: false,
  },
  [ACCESS.PAID]: {
    viewContent: true,
    contentLimit: Infinity,
    classroomTools: true,
    studentTracking: true,
    authoring: true,
    customContent: true,
    manageSeats: true,
  },
  [ACCESS.ADMIN]: {
    viewContent: true,
    contentLimit: Infinity,
    classroomTools: true,
    studentTracking: true,
    authoring: true,
    customContent: true,
    manageSeats: true,
  },
};

export function capabilities(level) {
  return CAPABILITIES[level] || CAPABILITIES[ACCESS.ANONYMOUS];
}

export function can(level, capability) {
  return Boolean(capabilities(level)[capability]);
}

/**
 * How many items from a list this person may see.
 */
export function limitList(items, contentLimit) {
  if (!Array.isArray(items)) return items;
  if (contentLimit === Infinity) return items;
  return items.slice(0, contentLimit);
}

/**
 * Reads a subscription record — canonical or legacy — into one shape.
 *
 * Both are accepted because the webhook writes both and either can be the
 * one that survived:
 *
 *   subscriptions/{uid}          { active, status, planKey, currentPeriodEnd }
 *   users/{uid}.subscription     { status, plan, ... }  (legacy)
 *
 * `active === true` is honoured on its own for records written before status
 * was stored at all.
 */
export function readSubscription(raw) {
  if (!raw || typeof raw !== "object") return null;

  const status = String(raw.status || "").toLowerCase();
  const live =
    raw.active === true || LIVE_SUBSCRIPTION_STATUSES.includes(status);

  // An explicit dead status beats a stale `active: true` left behind by an
  // earlier write — cancellation must win.
  const dead = ["canceled", "cancelled", "incomplete_expired", "unpaid"].includes(
    status
  );

  return {
    ...raw,
    status: status || (raw.active ? "active" : ""),
    live: live && !dead,
  };
}

/**
 * Registration writes a 14-day trial. This is the LOCAL trial, not a Stripe
 * one — no card, no subscription. It grants full access but is not evidence
 * of payment, which is why hasPaidSubscription ignores it.
 */
function hasActiveTrial(profile) {
  const trial = profile?.trial;
  if (!trial || trial.status !== "active") return false;
  const end = trial.endDate?.toDate ? trial.endDate.toDate() : trial.endDate;
  if (!end) return false;
  return new Date(end).getTime() > Date.now();
}

/**
 * Every signal that says "this person is a paying customer", and which one
 * answered. The source string is there so a support conversation can be
 * "what does the badge say?" instead of a Firestore console dig.
 *
 * @returns {{paid: boolean, source: string|null, status: string}}
 */
export function resolvePaidState({ subscription, profile }) {
  const canonical = readSubscription(subscription);
  if (canonical?.live) {
    return { paid: true, source: "subscriptions", status: canonical.status };
  }

  const legacy = readSubscription(profile?.subscription);
  if (legacy?.live) {
    return { paid: true, source: "users.subscription", status: legacy.status };
  }

  // A seat granted out of someone else's purchase. No subscription document
  // of their own exists at all.
  if (profile?.seatSourceOf) {
    return { paid: true, source: "seat", status: "seat" };
  }

  // Only the webhook and claimSeat can write a granted role, so its presence
  // means a payment cleared even if both subscription records are missing.
  if (isGrantedRole(profile?.role)) {
    return { paid: true, source: "role", status: canonical?.status || "granted" };
  }

  return {
    paid: false,
    source: null,
    status: canonical?.status || legacy?.status || "",
  };
}

/**
 * Full access to ONE course, for the 6-12 single-subject plans.
 *
 * This is deliberately not a new access LEVEL. A student who bought Chemistry
 * is still ACCESS.PAID; the question "which courses?" is orthogonal to
 * "how much of the product do they get?", and folding it into the level
 * enum would mean every existing `level === ACCESS.PAID` check silently
 * changes meaning.
 *
 * It layers on top of the free tier rather than replacing it: a student who
 * bought Chemistry sees all of Chemistry and the first FREE_TOPIC_LIMIT
 * topics of Biology. That's the upsell surface, and it costs nothing to
 * build because the free tier already works that way.
 *
 * Every paid signal EXCEPT a subject-scoped canonical subscription means full
 * access. Seats, granted roles, legacy records and the local trial carry no
 * course information at all — treating "no information" as "no access" would
 * lock out every one of them.
 */
function resolveCourseAccess({ level, subscription, profile, track }) {
  if (level === ACCESS.ADMIN) return true;
  if (level !== ACCESS.PAID) return false;

  const canonical = readSubscription(subscription);

  // Not a subject-scoped subscription — some other signal granted PAID, and
  // all of those mean everything.
  if (!canonical?.live || canonical.scope !== "subject") return true;

  return subscriptionCoversCourse(canonical, track);
}

function levelFromState({ user, isAdmin, subscription, profile }) {
  if (!user) return ACCESS.ANONYMOUS;
  if (isAdmin) return ACCESS.ADMIN;
  if (!isEmailConfirmed(user)) return ACCESS.UNVERIFIED;

  if (resolvePaidState({ subscription, profile }).paid) return ACCESS.PAID;
  if (hasActiveTrial(profile)) return ACCESS.PAID;

  return ACCESS.FREE;
}

export default function useAccessLevel() {
  const [state, setState] = useState({
    loading: true,
    level: ACCESS.ANONYMOUS,
    user: null,
    subscription: null,
    profile: null,
    isAdmin: false,
  });

  useEffect(() => {
    let unsubSubscription = null;
    let unsubProfile = null;

    const cleanupListeners = () => {
      if (unsubSubscription) unsubSubscription();
      if (unsubProfile) unsubProfile();
      unsubSubscription = null;
      unsubProfile = null;
    };

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      cleanupListeners();

      if (!user) {
        setState({
          loading: false,
          level: ACCESS.ANONYMOUS,
          user: null,
          subscription: null,
          profile: null,
          isAdmin: false,
        });
        return;
      }

      // Admin comes from a custom claim, not a Firestore field — a user can
      // edit their own profile doc but cannot mint themselves a claim.
      const token = await user.getIdTokenResult(true).catch(() => null);
      const isAdmin = Boolean(token?.claims?.admin);

      let subscription = null;
      let profile = null;
      let sawSubscription = false;
      let sawProfile = false;

      const push = () => {
        // Hold "loading" until both listeners have reported once. Otherwise a
        // paying user flashes as FREE for a beat before their subscription
        // arrives — long enough for a guard to bounce them off a page they
        // just paid for.
        if (!sawSubscription || !sawProfile) return;
        setState({
          loading: false,
          level: levelFromState({ user, isAdmin, subscription, profile }),
          user,
          subscription,
          profile,
          isAdmin,
        });
      };

      unsubSubscription = onSnapshot(
        doc(db, "subscriptions", user.uid),
        (snap) => {
          subscription = snap.exists() ? snap.data() : null;
          sawSubscription = true;
          push();
        },
        (err) => {
          // Almost always a Firestore rules rejection. Worth a console line:
          // silently swallowing it is what made a paid account look free with
          // no clue why. The legacy field on the profile still gets a chance
          // to answer.
          console.warn(
            "[useAccessLevel] can't read subscriptions/{uid} —",
            err?.code || err?.message
          );
          subscription = null;
          sawSubscription = true;
          push();
        }
      );

      unsubProfile = onSnapshot(
        doc(db, "users", user.uid),
        (snap) => {
          profile = snap.exists() ? snap.data() : null;
          sawProfile = true;
          push();
        },
        (err) => {
          console.warn(
            "[useAccessLevel] can't read users/{uid} —",
            err?.code || err?.message
          );
          profile = null;
          sawProfile = true;
          push();
        }
      );
    });

    return () => {
      cleanupListeners();
      unsubAuth();
    };
  }, []);

  const caps = capabilities(state.level);
  const paidState = resolvePaidState({
    subscription: state.subscription,
    profile: state.profile,
  });
  const roleKind = effectiveRoleKind(state.profile || {}, {
    isAdmin: state.isAdmin,
  });

  return {
    ...state,
    ...caps,
    isPaid: state.level === ACCESS.PAID || state.level === ACCESS.ADMIN,

    // "Has full access" and "has actually paid" are different questions.
    // The local signup trial returns ACCESS.PAID, so isPaid is true during it
    // even though no money has moved — this is the money question, and it's
    // what hides the Subscribe prompt.
    hasPaidSubscription: paidState.paid,
    subscriptionStatus: paidState.status,   // "trialing", "active", "seat", ""
    paidVia: paidState.source,              // which signal answered

    // A Stripe trial is a paid subscription that hasn't billed yet. Useful
    // for wording: "Trial · billing starts 13 Sep".
    isStripeTrialing: paidState.paid && paidState.status === "trialing",

    // Which shape of the product this person gets. Not an access decision —
    // see utils/accountRole.js.
    roleKind,
    isEducatorAccount: roleKind === ROLE_KIND.EDUCATOR,
    portalView: portalViewFor(state.profile || {}, { isAdmin: state.isAdmin }),

    isFree: state.level === ACCESS.FREE,
    can: (capability) => can(state.level, capability),

    // ---- Per-course access, for the 6-12 single-subject plans ----
    //
    // `track` is the course or grade token in the :grade slot of the portal
    // URL — "algebra-1", "5th". See src/data/courses.js.
    canAccessCourse: (track) =>
      resolveCourseAccess({
        level: state.level,
        subscription: state.subscription,
        profile: state.profile,
        track,
      }),

    // What DynamicPortalPage should slice the topic list to. Replaces a bare
    // `contentLimit` read, which can't tell the difference between a student
    // who bought this course and one who bought a different one.
    contentLimitForCourse: (track) =>
      resolveCourseAccess({
        level: state.level,
        subscription: state.subscription,
        profile: state.profile,
        track,
      })
        ? Infinity
        : caps.contentLimit,

    // Which courses a subject-scoped subscription paid for. [] means either
    // all-subjects or not subscribed — check hasPaidSubscription to tell
    // those apart.
    purchasedCourses: purchasedCourses(state.subscription),
  };
}
